import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Alert, Dialog, DialogContent, Grid, TextField, MenuItem,
  IconButton, Typography, Chip, Divider, Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import LockResetIcon from '@mui/icons-material/LockReset';
import { DataTable } from '../common/DataTable';
import { SearchableEntitySelect, EntityOption } from '../common/SearchableEntitySelect';
import { userAPI, branchAPI, roleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';

const STAFF_ROLES = [
  { label: 'Manager', value: 'manager' },
  { label: 'Operator', value: 'operator' },
  { label: 'Supervisor', value: 'supervisor' },
  { label: 'Employee', value: 'employee' },
];

const emptyForm = () => ({
  name: '',
  email: '',
  mobile: '',
  designation: '',
  role: '',
  branch: '',
  staffRole: 'employee',
  status: 'active',
  password: '',
});

export const UsersPanel: React.FC = () => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [open, setOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [branches, setBranches] = useState<EntityOption[]>([]);
  const [roles, setRoles] = useState<EntityOption[]>([]);

  const canCreate = hasPermission('roles', 'create');
  const canEdit = hasPermission('roles', 'edit');
  const canDelete = hasPermission('roles', 'delete');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await userAPI.getAll();
      setRows(res.data);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([branchAPI.getAll(), roleAPI.getAll()]).then(([b, r]) => {
      setBranches((b.data || []).map((x: Record<string, string>) => ({
        _id: String(x._id), label: x.branchName, sublabel: x.branchCode,
      })));
      setRoles((r.data || []).map((x: Record<string, string>) => ({
        _id: String(x._id), label: x.roleName, sublabel: x.description,
      })));
    }).catch(() => {});
  }, []);

  const openCreate = () => {
    setForm(emptyForm());
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    setForm({
      name: String(row.name || ''),
      email: String(row.email || ''),
      mobile: String(row.mobile || ''),
      designation: String(row.designation || ''),
      role: String((row.role as { _id?: string })?._id || row.role || ''),
      branch: String((row.branch as { _id?: string })?._id || row.branch || ''),
      staffRole: String(row.staffRole || 'employee'),
      status: String(row.status || 'active'),
      password: '',
    });
    setEditId(String(row._id));
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (!form.name.trim() || !form.email.trim()) {
        setError('Name and email are required');
        return;
      }
      if (!form.role) {
        setError('App role (permissions) is required');
        return;
      }
      if (!form.branch) {
        setError('Branch is required');
        return;
      }
      if (!editId && (!form.password || form.password.length < 6)) {
        setError('Password must be at least 6 characters');
        return;
      }
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile,
        designation: form.designation,
        role: form.role,
        branch: form.branch,
        staffRole: form.staffRole,
        status: form.status,
        ...(editId ? {} : { password: form.password }),
      };
      if (editId) {
        await userAPI.update(editId, payload);
        showToast('User updated');
      } else {
        await userAPI.create(payload);
        showToast('User created');
      }
      setOpen(false);
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: Record<string, unknown>) => {
    if (!window.confirm(`Delete user ${row.name}?`)) return;
    try {
      await userAPI.remove(String(row._id));
      showToast('User deleted');
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Delete failed');
    }
  };

  const handleResetPassword = async () => {
    if (!editId || resetPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      await userAPI.resetPassword(editId, resetPassword);
      showToast('Password updated');
      setResetOpen(false);
      setResetPassword('');
    } catch {
      setError('Password reset failed');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        {canCreate && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ ...catchyPrimaryButtonSx, background: brandCss.gradientPrimary }}>
            Add User
          </Button>
        )}
      </Box>

      {error && !open && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

      <DataTable
        columns={[
          {
            field: 'name',
            header: 'Name',
            render: (row: Record<string, unknown>) => {
              const name = String(row.name || '—');
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{
                    width: 26, height: 26, fontSize: 11, fontWeight: 700,
                    bgcolor: brandCss.green100, color: brandCss.primaryDark,
                  }}>
                    {name !== '—' ? name.charAt(0).toUpperCase() : '?'}
                  </Avatar>
                  <Typography fontWeight={600} fontSize={13}>{name}</Typography>
                </Box>
              );
            },
          },
          { field: 'email', header: 'Email' },
          { field: 'roleName', header: 'App Role' },
          { field: 'branchName', header: 'Branch' },
          { field: 'staffRole', header: 'Staff Type' },
          { field: 'designation', header: 'Designation' },
          {
            field: 'status',
            header: 'Status',
            render: (row: Record<string, unknown>) => (
              <Chip size="small" label={String(row.status || 'active')}
                color={row.status === 'active' ? 'success' : 'default'} variant="outlined" />
            ),
          },
          {
            field: '_actions',
            header: 'Actions',
            render: (row: Record<string, unknown>) => (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {canEdit && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                {canDelete && (
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row); }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          },
        ]}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={(v: string) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        recordLabel="users"
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: `${RADIUS}px` } }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', bgcolor: brandCss.green50 }}>
          <Typography fontWeight={800}>{editId ? 'Edit User' : 'Add User'}</Typography>
          <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Full Name" required fullWidth size="small" sx={catchyTextFieldSx}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email" type="email" required fullWidth size="small" sx={catchyTextFieldSx}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={Boolean(editId)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SearchableEntitySelect label="App Role (Permissions)" placeholder="Select role..."
                value={form.role} options={roles} onChange={(v: string) => setForm({ ...form, role: v })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SearchableEntitySelect label="Branch" placeholder="Select branch..."
                value={form.branch} options={branches} onChange={(v: string) => setForm({ ...form, branch: v })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Staff Type" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.staffRole} onChange={(e) => setForm({ ...form, staffRole: e.target.value })}>
                {STAFF_ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Mobile" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Designation" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            {!editId && (
              <Grid item xs={12}>
                <TextField label="Password" type="password" required fullWidth size="small" sx={catchyTextFieldSx}
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  helperText="Min 6 characters" />
              </Grid>
            )}
            {editId && canEdit && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Button startIcon={<LockResetIcon />} onClick={() => setResetOpen(true)} size="small">
                  Reset Password
                </Button>
              </Grid>
            )}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
              sx={{ ...catchyPrimaryButtonSx, background: brandCss.gradientPrimary }}>
              {editId ? 'Update User' : 'Create User'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: 3 }}>
          <Typography fontWeight={700} mb={2}>Reset Password</Typography>
          <TextField label="New Password" type="password" fullWidth size="small" sx={catchyTextFieldSx}
            value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleResetPassword}>Save Password</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
